import React from 'react';
import { QRCode } from 'antd';
import { useTranslation } from 'react-i18next';
import Button from 'components/Reusable/Button';
import LoadingIcon from 'icons/loaders/tail-spin.svg';
import BackArrow from 'icons/arrow-left.svg';
import {
  MfaChallengeAction,
  MfaNudgeAction
} from '@anghami/neogateway/dist/endpoints/authentication';
import styles from './mfaChallenge.module.scss';
import loginStyles from 'pages/Landing/Login/login.module.scss';
import commonStyles from 'components/Landing/frames/common.module.scss';
import { combineClassNames } from 'helpers/styling-helper';

interface IProps {
  action: MfaChallengeAction | MfaNudgeAction;
  otpauthUrl?: string | null;
  loading?: boolean;
  error?: string;
  onSubmit: (otp: string) => void;
  onSkip?: () => void;
  onBack?: () => void;
}

export default function MfaChallenge({
  action,
  otpauthUrl,
  loading,
  error,
  onSubmit,
  onSkip,
  onBack
}: IProps) {
  const { t } = useTranslation('common');
  const [otp, setOtp] = React.useState('');
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const isEnroll = action === 'enroll_required' || action === 'enroll_optional';

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const updateOtp = (value: string, focusIndex?: number) => {
    setOtp(value);
    if (focusIndex === undefined) return;
    requestAnimationFrame(() => focusInput(Math.min(Math.max(focusIndex, 0), 5)));
  };

  const handleOtpChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    if (!digits) {
      const nextOtp = `${otp.slice(0, index)}${otp.slice(index + 1)}`;
      updateOtp(nextOtp);
      return;
    }

    if (digits.length > 1) {
      const nextOtp = `${otp.slice(0, index)}${digits}${otp.slice(index + digits.length)}`
        .slice(0, 6)
        .padEnd(otp.length, '');
      updateOtp(nextOtp.slice(0, 6), Math.min(index + digits.length, 5));
      return;
    }

    const nextOtp = `${otp.slice(0, index)}${digits}${otp.slice(index + 1)}`.slice(0, 6);
    updateOtp(nextOtp, index < 5 ? index + 1 : index);
  };

  const handleOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
      return;
    }
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handleSubmit = () => {
    if (otp.trim().length !== 6 || loading) return;
    onSubmit(otp);
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!digits) return;
    event.preventDefault();
    updateOtp(digits, digits.length === 6 ? 5 : digits.length);
  };

  return (
    <div className={styles.mfaChallenge}>
      <h1 className={combineClassNames(commonStyles.landingHeader, styles.title)}>
        {onBack && <BackArrow className={commonStyles.backIcon} onClick={onBack} />}
        {isEnroll ? t('Set up two-factor authentication') : t('Enter your verification code')}
      </h1>
      <p className={styles.description}>
        {isEnroll ? (
          <>
            {t('Scan this QR code with an authenticator app')}{' '}
            (
            <a
              className={styles.inlineLink}
              href="https://apps.apple.com/us/app/google-authenticator/id388497605"
              target="_blank"
              rel="noreferrer"
            >
              {t('Google Authenticator')}
            </a>
            ,{' '}
            <a
              className={styles.inlineLink}
              href="https://authy.com/download/"
              target="_blank"
              rel="noreferrer"
            >
              {t('Authy')}
            </a>
            , etc.), {t('then enter the 6-digit code it shows.')}
          </>
        ) : (
          t('Open your authenticator app and enter the 6-digit code.')
        )}
      </p>
      <div className={styles.centeredContent}>
        {isEnroll && otpauthUrl && (
          <div className={styles.qrWrapper}>
            <QRCode value={otpauthUrl} size={176} bordered={false} />
          </div>
        )}
        <label className={styles.codeLabel} htmlFor="otp-0">
          {t('Verification code')}
        </label>
        <div className={styles.otpGroup} dir="ltr">
          {Array.from({ length: 6 }).map((_, index) => (
            <input
              key={index}
              id={index === 0 ? 'otp-0' : undefined}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              className={styles.otpBox}
              value={otp[index] || ''}
              onChange={(event) => handleOtpChange(index, event)}
              onKeyDown={(event) => handleOtpKeyDown(index, event)}
              onPaste={handleOtpPaste}
              maxLength={1}
              aria-label={`${t('Verification code')} ${index + 1}`}
            />
          ))}
        </div>
        {error && <span className={styles.errorMessage}>{error}</span>}
        <Button
          customClasses={[loginStyles.customButton, styles.actionButton, 'purpleFilled']}
          onSubmit={handleSubmit}
          disabled={otp.trim().length !== 6 || loading}
          icon={
            loading ? (
              <LoadingIcon color={'white'} style={{ margin: 'unset' }} width={20} height={20} />
            ) : (
              <></>
            )
          }
          label={t('Verify')}
        />
        {onSkip && (
          <span className={styles.skipLink} onClick={loading ? undefined : onSkip}>
            {t('Skip for now')}
          </span>
        )}
      </div>
    </div>
  );
}
